// © 2026 AttainIP Engineering
/**
 * Route guard that restores session from httpOnly cookie, then
 * redirects unauthenticated users to /login.
 */
import { type ReactNode, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Spinner } from '@/components/Spinner';
import { useAuthStore, getStoredRefreshToken } from '@/store';
import { restoreSessionFromRefreshToken } from '@/auth/restoreSession';

interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuthStore();
  const attemptedRestore = useRef(false);

  useEffect(() => {
    if (isAuthenticated || attemptedRestore.current) return;
    attemptedRestore.current = true;

    // No stored refresh token — straight to login.
    if (!getStoredRefreshToken()) {
      navigate('/login', { state: { from: location }, replace: true });
      return;
    }

    void (async () => {
      const result = await restoreSessionFromRefreshToken();
      if (!result.ok) {
        navigate('/login', { state: { from: location }, replace: true });
      }
    })();
  }, [isAuthenticated, location, navigate]);

  if (!isAuthenticated && (isLoading || !attemptedRestore.current)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size={32} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
